/**
 * @author Brendan Magee
 * @desc form for easy onboarding of notification rules
 * @example <easy-notification-form easy-notifications="easyNotifications">
 *          </easy-notification-form>
 */

import template from './easyNotificationForm.html';

function EasyNotificationFormController(
  $translate,
  $log,
  notificationService,
  peopleService,
  toastWrapperService,
) {
  this.$onInit = () => {
    this.easyNotificationTypes = {
      story_publish_author: {
        type: 'story_publish',
        creators: []
      },
      task_create_author: {
        type: 'task_create',
        story: {
          creators: []
        }
      },
      task_claim_author: {
        type: 'task_claim',
        story: {
          creators: []
        }
      },
      task_claim_creator: {
        type: 'task_claim',
        createdBy: null
      }
      // pitch_create_author: {
      //   type: 'pitch_create',
      //   story: {
      //     creators: []
      //   }
      // },
      // publication_pitch_create_author: {
      //   type: 'publication_pitch_create',
      //   story: {
      //     creators: []
      //   }
      // }
    };

    /**
     * The `pitch_create_author` and `publication_pitch_create_author` types above require changes
     * to the API before they can be uncommented and subsequently added to the UI.
     */

    this.missingData = {
      byline: false,
      userObject: false,
      authorService: false
    };

    this.labels = {
      story_publish_author: $translate.instant('EASY_NOTIFICATIONS.MY_STORY_PUBLISHES'),
      task_create_author: $translate.instant('EASY_NOTIFICATIONS.TASK_CREATED_MY_STORY'),
      task_claim_author: $translate.instant('EASY_NOTIFICATIONS.TASK_CLAIMED_MY_STORY'),
      task_claim_creator: $translate.instant('EASY_NOTIFICATIONS.TASK_I_CREATED_CLAIMED'),
      pitch_create_author: $translate.instant('EASY_NOTIFICATIONS.PLATFORM_PITCH_CREATED'),
      publication_pitch_create_author: $translate.instant('EASY_NOTIFICATIONS.PUBLICATION_PITCH_CREATED')
    };

    this.notifications = Object.keys(this.easyNotificationTypes).map(type => ({
      type,
      name: this.labels[type] || type,
      active: false
    }));

    this.easyNotifications.forEach((notification) => {
      const { easyNotificationType } = notification;
      this.easyNotificationTypes[easyNotificationType].id = notification.id;
      this.notifications.find(not => not.type === easyNotificationType).active = true;
    });

    peopleService.getUser().then((user) => {
      this.user = user;

      if (this.user && this.user.preferences) {
        const { defaultNotificationTarget } = user.preferences;
        this.target = defaultNotificationTarget;
      } else {
        this.target = null;
      }

      peopleService.getAuthorByEmail(user.email).then((authorData) => {
        if (authorData.count) {
          const [current] = authorData.q_results;
          this.byline = {
            id: current._id,
            name: current.byline
          };
        } else {
          this.missingData.byline = true;
        }
      }, () => {
        this.missingData.authorService = true;
        $log.error('Error getting data from author service');
      });
    }, () => {
      this.missingData.userObject = true;
      $log.error('Error getting the user object');
    });
  };

  this.update = (notification) => {
    const alreadySubscribed = !!this.easyNotificationTypes[notification.type].id;

    if (notification.active && !alreadySubscribed) {
      this.subscribe(notification);
    } else if (alreadySubscribed) {
      this.unsubscribe(notification);
    }
  };

  this.subscribe = (notification) => {
    const current = this.easyNotificationTypes[notification.type];

    if (!this.bylineMissing
        && current.story
        && current.story.creators
        && !current.story.creators.length) {
      current.story.creators.push(this.byline.id);
    } else if (!this.bylineMissing
               && current.creators
               && !current.creators.length) {
      current.creators.push(this.byline.id);
    } else if (current.createdBy === null) {
      current.createdBy = this.user.username;
    }

    const payload = {
      recipient: {
        type: 'user',
        id: this.user.username
      },
      trigger: current,
      target: this.target,
      easyNotificationType: notification.type
    };

    notificationService.create(payload).then((data) => {
      this.easyNotificationTypes[data.easyNotificationType].id = data.id;

      toastWrapperService.createToast($translate.instant('EASY_NOTIFICATIONS.SUBSCRIBED'), 'success');
      $log.log(`Subscribed to ${notification.type}`);
    }, (response) => {
      this.notifications.find(item => item.type === notification.type).active = false;

      const error = $translate.instant('ERROR');
      const errorMsg = response && response.data && response.data.error
        ? `${error}: ${response.data.error}`
        : error;

      toastWrapperService.createToast(errorMsg, 'danger');
      $log.error(`Error subscribing to ${notification.type}`);
    });
  };

  this.unsubscribe = (notification) => {
    notificationService.delete(this.easyNotificationTypes[notification.type]).then(() => {
      this.easyNotificationTypes[notification.type].id = null;
      this.notifications.find(item => item.type === notification.type).active = false;

      toastWrapperService.createToast($translate.instant('EASY_NOTIFICATIONS.UNSUBSCRIBED'), 'success');
      $log.log(`Unsubscribed from ${notification.type}`);
    }, () => {
      this.notifications.find(item => item.type === notification.type).active = true;

      toastWrapperService.createToast($translate.instant('ERROR'), 'danger');
      $log.error(`Error unsubscribing from ${notification.type}`);
    });
  };
}

const easyNotificationForm = {
  template,
  bindings: {
    easyNotifications: '<'
  },
  controller: 'EasyNotificationFormController'
};

const controllerArray = [
  '$translate',
  '$log',
  'notificationService',
  'peopleService',
  'toastWrapperService',
  EasyNotificationFormController
];

export default function (ngModule) {
  ngModule.component('easyNotificationForm', easyNotificationForm);
  ngModule.controller('EasyNotificationFormController', controllerArray);
}
